import { Badge } from '@kerfjs/ui/badge';
import { ContentItem } from '@kerfjs/ui/content-item';
import { List } from '@kerfjs/ui/list';
import { Row } from '@kerfjs/ui/row';
import { Text } from '@kerfjs/ui/text';
import type { SafeHtml } from 'kerfjs';

import type { MatrixVendorColumn } from '../../../data/api.js';
import { AiDetectedLabel, quoteFromAi } from './ai-detected-label.js';

export interface VendorCardProps {
  column: MatrixVendorColumn;
  baseCurrency: string;
  lineCount: number;
  readOnly: boolean;
}

/** Quotation vendor and commercial terms, with optional quote-editor activation. */
export function VendorCard({ column: col, baseCurrency, lineCount, readOnly }: VendorCardProps): SafeHtml {
  const locType = [vendorLocation(col), col.vendorType].filter((x): x is string => x !== null && x !== '');
  const basisUnknown = col.priceBasis === null || col.priceBasis === 'unknown';
  // Date · currency · basis · payment terms (basis omitted here when unknown — shown as a warn chip instead).
  const terms = [
    col.quoteDate !== null ? shortQuoteDate(col.quoteDate) : null,
    col.currency ?? baseCurrency,
    basisUnknown ? null : basisLabel(col.priceBasis),
    col.paymentTerms,
  ].filter((x): x is string => x !== null && x !== '');
  const details = (
    <List gap="2xs">
      <Row gap="2xs" vAlign="middle" wrap>
        <strong>{col.vendorName}</strong>
        <AiDetectedLabel detected={quoteFromAi(col.howReceived)} />
      </Row>
      {locType.length > 0 ? (
        <Text variant="span" tone="quiet" size="compact">
          {locType.join(' · ')}
        </Text>
      ) : null}
      {basisUnknown ? (
        <Row gap="2xs" vAlign="middle" wrap>
          {terms.length > 0 ? (
            <Text variant="span" tone="quiet" size="compact">
              {terms.join(' · ')} ·
            </Text>
          ) : null}
          <Badge tone="warning" shape="pill" size="compact">
            basis unknown
          </Badge>
        </Row>
      ) : (
        <Text variant="span" tone="quiet" size="compact">
          {terms.join(' · ')}
        </Text>
      )}
      <Text variant="span" tone="quiet" size="compact">
        {String(lineCount)} line{lineCount === 1 ? null : 's'} quoted
      </Text>
    </List>
  );
  // Both are Kerf interactive cards (HS2-CY4F20): an editable one opens the quotation editor, a
  // read-only one opens the same details view-only (HS2-MKHVY9).
  return readOnly ? (
    <ContentItem
      interactive
      action="quote-view"
      itemId={col.quotationId}
      frame="framed"
      ariaLabel={`View Quotation from ${col.vendorName}`}
      title="View this quotation"
      className="vendor-card"
      appearance="surface"
      rootAttributes={{ 'data-key': col.quotationId }}
    >
      {details}
    </ContentItem>
  ) : (
    <ContentItem
      interactive
      action="quote-edit"
      itemId={col.quotationId}
      frame="framed"
      ariaLabel={`Edit Quotation from ${col.vendorName}`}
      title="Edit this quotation"
      className="vendor-card"
      appearance="surface"
      rootAttributes={{ 'data-key': col.quotationId }}
    >
      {details}
    </ContentItem>
  );
}

/** Compact quote date for the vendor card, e.g. "8 Sep 2026". */
function shortQuoteDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Readable price-basis label for the vendor card (HS2-8CXQXS). */
function basisLabel(basis: string | null): string {
  if (basis === 'delivered-to-site') return 'delivered';
  return basis ?? '';
}

/** The vendor card's location line: the vendor's city (or country), and for a
 *  comparison-sheet-sourced quote with no vendor location, "from comparison sheet". */
function vendorLocation(col: MatrixVendorColumn): string | null {
  return (
    col.vendorCity ?? col.vendorCountry ?? (col.howReceived === 'comparison-sheet' ? 'from comparison sheet' : null)
  );
}
